import { Modal, message } from 'antd';

export const useContentListListActions = ({
  selectedRowKeys,
  setSelectedRowKeys,
  deleteContent,
  downloadContent,
  reloadListAfterDelete,
  loadSearchData,
  pagination,
  onDelete,
  onDownload,
  onFilter,
}) => {
  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除内容ID为 "${id}" 的内容吗？`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteContent({
            content_ids: [id],
          });

          if (response.status === 'success') {
            if (response.deleted_ids && response.deleted_ids.length > 0) {
              message.success('删除成功');
              setSelectedRowKeys((prev) => (prev || []).filter((key) => key !== id));
              await reloadListAfterDelete([id]);
              if (onDelete) onDelete(id);
              return Promise.resolve();
            }
            if (response.not_found_ids && response.not_found_ids.length > 0) {
              message.warning('内容不存在或已被删除');
              await reloadListAfterDelete([id]);
              return Promise.resolve();
            }
            message.warning('删除失败');
            return Promise.reject(new Error('删除失败'));
          }
          message.error('删除失败');
          return Promise.reject(new Error('删除失败'));
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
          return Promise.reject(error);
        }
      },
    });
  };

  const handleDownload = async (record) => {
    try {
      message.loading({ content: '正在下载...', key: 'download', duration: 0 });
      const response = await downloadContent(record.content_id);
      if (response && response.download_url) {
        window.open(response.download_url, '_blank');
        message.success({ content: '下载成功', key: 'download' });
      } else if (response && response.status === 'success') {
        message.success({ content: '下载成功', key: 'download' });
      } else {
        message.error({ content: '下载失败', key: 'download' });
      }
      if (onDownload) onDownload(record);
    } catch (error) {
      console.error('下载失败:', error);
      message.error({ content: '下载失败，请稍后重试', key: 'download' });
    }
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的内容');
      return;
    }

    const contentIds = [...selectedRowKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条内容吗？`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteContent({
            content_ids: contentIds,
          });

          if (response.status === 'success') {
            if (response.deleted_ids && response.deleted_ids.length > 0) {
              message.success(`成功删除 ${response.deleted_ids.length} 条内容`);
              setSelectedRowKeys([]);
              await reloadListAfterDelete(contentIds);
              if (onDelete) contentIds.forEach((id) => onDelete(id));
              return Promise.resolve();
            }
            if (response.not_found_ids && response.not_found_ids.length > 0) {
              message.warning(`有 ${response.not_found_ids.length} 条内容不存在或已被删除`);
              await reloadListAfterDelete(contentIds);
              return Promise.resolve();
            }
            message.warning('删除失败');
            return Promise.reject(new Error('删除失败'));
          }
          message.error('删除失败');
          return Promise.reject(new Error('删除失败'));
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
          return Promise.reject(error);
        }
      },
    });
  };

  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要下载的内容');
      return;
    }

    try {
      message.loading({ content: `正在下载 ${selectedRowKeys.length} 个文件...`, key: 'batchDownload', duration: 0 });

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < selectedRowKeys.length; i += 1) {
        const contentId = selectedRowKeys[i];
        try {
          if (i > 0) await new Promise((resolve) => setTimeout(resolve, 500));
          const response = await downloadContent(contentId);
          if (response && (response.download_url || response.status === 'success')) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        } catch (error) {
          console.error(`下载内容 ${contentId} 失败:`, error);
          failCount += 1;
        }
      }

      message.destroy('batchDownload');
      if (successCount > 0 && failCount === 0) {
        message.success(`成功下载 ${successCount} 个文件`);
        setSelectedRowKeys([]);
      } else if (successCount > 0 && failCount > 0) {
        message.warning(`成功下载 ${successCount} 个文件，失败 ${failCount} 个`);
        setSelectedRowKeys([]);
      } else {
        message.error('批量下载失败');
      }
    } catch (error) {
      console.error('批量下载失败:', error);
      message.destroy('batchDownload');
      message.error('批量下载失败，请稍后重试');
    }
  };

  const handleSearch = async () => {
    await loadSearchData(pagination.current, pagination.pageSize);
  };

  const handleFilterFinish = (values) => {
    const {
      tags,
      ingested_at_range,
      ...restValues
    } = values;

    const payload = {
      ...restValues,
      tags: tags
        ? tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
        : undefined,
      ingested_at_range: ingested_at_range
        ? {
          from: (ingested_at_range[0]?.toDate ? ingested_at_range[0].toDate() : ingested_at_range[0]).toISOString(),
          to: (ingested_at_range[1]?.toDate ? ingested_at_range[1].toDate() : ingested_at_range[1]).toISOString(),
        }
        : undefined,
    };

    if (onFilter) {
      onFilter(payload);
    } else {
      console.log('筛选条件：', payload);
    }
  };

  return {
    handleDelete,
    handleDownload,
    handleBatchDelete,
    handleBatchDownload,
    handleSearch,
    handleFilterFinish,
  };
};
